/**
 * Centralized auth token helper.
 *
 * Single source of truth for getting the current Firebase ID token.
 * Replaces the `supabase.auth.getSession() → session.access_token` pattern.
 *
 * Firebase's getIdToken() auto-refreshes expired tokens, so no manual
 * refresh logic is needed (unlike the Supabase Safari workarounds).
 */

import { firebaseAuth } from './firebase';

/**
 * Get the current Firebase ID token, or null if not authenticated.
 * The token is automatically refreshed if expired.
 */
export async function getAuthToken(): Promise<string | null> {
  const user = firebaseAuth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

/**
 * Get the current Firebase ID token, or throw if not authenticated.
 * Use this in authenticated-only code paths.
 */
export async function getAuthTokenOrThrow(): Promise<string> {
  const token = await getAuthToken();
  if (!token) throw new Error('No active session. Please sign in again.');
  return token;
}

/**
 * Get the current user's UID, or null if not authenticated.
 */
export function getCurrentUserId(): string | null {
  return firebaseAuth.currentUser?.uid ?? null;
}

/**
 * Is the current session an admin "view as user" impersonation? Reads the
 * `impersonation` claim the payment service mints (routes/impersonate.ts) from
 * the token result Firebase caches. False when signed out.
 */
export async function isImpersonationSession(): Promise<boolean> {
  const user = firebaseAuth.currentUser;
  if (!user) return false;
  const result = await user.getIdTokenResult();
  return result.claims.impersonation === true;
}

/**
 * Get a compatible user object { id, email }, or null if not authenticated.
 * Drop-in replacement for `supabase.auth.getUser()` → `data.user`.
 */
export function getCurrentUser(): { id: string; email: string | null } | null {
  const user = firebaseAuth.currentUser;
  if (!user) return null;
  return { id: user.uid, email: user.email };
}
